import { filter, set } from 'lodash';
import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Tooltip,
  IconButton,
  Collapse,
  Box,
} from '@mui/material';
import axios from 'axios';
import { id } from 'date-fns/locale';

// components
import Page from '../components/Page';
import SearchNotFound from '../components/SearchNotFound';
import { UserListHead, SeniorCitizenListToolbar, SeniorCitizenUserViewDialog, UserMoreMenu, SeniorCitizenUserDialog } from '../sections/@dashboard/user';

import Iconify from '../components/Iconify';
import Scrollbar from '../components/Scrollbar';

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query, categoryFilter) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  let filteredArray = stabilizedThis.map((el) => el[0]);

  // Filter by email
  if (query) {
    return filter(stabilizedThis.map((el) => el[0]), (_user) =>
      _user.email.toLowerCase().includes(query.toLowerCase())
    );
  }

  if (categoryFilter) {
    filteredArray = filteredArray.filter((user) =>
      user.category === categoryFilter);
  }

  return filteredArray;
}

// Define the table headers to include all fields from the schema
const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'fatherOrHusbandName', label: 'Father/Husband Name', alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'phoneNumber', label: 'Phone Number', alignRight: false },
  { id: 'address', label: 'Address', alignRight: false },
  { id: 'pinCode', label: 'Pin Code', alignRight: false },
  { id: 'category', label: 'Category', alignRight: false },
  { id: 'gender', label: 'Gender', alignRight: false },
  { id: 'dateOfBirth', label: 'Date of Birth', alignRight: false },
  { id: 'age', label: 'Age', alignRight: false },
  { id: 'qualification', label: 'Qualification', alignRight: false },
  { id: 'currentDesignation', label: 'Current Designation', alignRight: false },
  { id: 'experienceYears', label: 'Experience (Years)', alignRight: false },
  { id: 'pastExperience', label: 'Past Experience', alignRight: false },
  { id: 'papersPublished', label: 'Papers Published', alignRight: false },
  { id: 'memberships', label: 'Memberships', alignRight: false },
  { id: 'organization.name', label: 'Organization Name', alignRight: false },
  { id: 'organization.address', label: 'Organization Address', alignRight: false },
  { id: 'organization.phone', label: 'Organization Phone', alignRight: false },
  { id: 'organization.mobile', label: 'Organization Mobile', alignRight: false },
  { id: 'organization.email', label: 'Organization Email', alignRight: false },
  { id: 'preferredMailingAddress', label: 'Preferred Mailing Address', alignRight: false },
  { id: 'additionalInfo', label: 'Additional Info', alignRight: false },
  { id: 'introducedBy', label: 'Introduced By', alignRight: false },
  { id: 'paymentdetails', label: 'Payment Details', alignRight: false },
  { id: 'MSMECertificate', label: 'MSME Certificate', alignRight: false },
  { id: 'panNumber', label: 'PAN Number', alignRight: false },
  { id: 'companyName', label: 'Company Name', alignRight: false },
  { id: 'natureOfIndustry', label: 'Nature of Industry', alignRight: false },
  { id: 'numberOfEmployees', label: 'Number of Employees', alignRight: false },
  { id: 'howDidYouKnowAboutUs', label: 'How Did You Know About Us', alignRight: false },
  { id: 'membershipID', label: 'Membership ID', alignRight: false },
  { id: 'membershipCategory', label: 'Membership Category', alignRight: false },
  { id: 'tenure', label: 'Tenure', alignRight: false },
  { id: 'action', label: 'Action', alignRight: false },
];
  
// ----------------------------------------------------------------------
  
export default function SeniorCitizenUser() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [users, setUsers] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  
  useEffect(() => {
    fetchUsers();
  }, []);
  
  const fetchUsers = async () => {
    try {
      const response = await axios.get('https://form.cpccbe.org/api/seniorcitizen/seniorcitizen'); // Adjust endpoint as necessary
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };
  
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = users.map((n) => n.email);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };
  
  const handleClick = (event, email) => {
    const selectedIndex = selected.indexOf(email);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, email);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleOpenModal = (userId) => {
    setEditingUserId(userId);
    setOpenModal(true);
  };
     
  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingUserId(null);
  };

  const handleEditUser = async (userId, updatedData) => {
    try {
      await axios.put(`https://form.cpccbe.org/api/seniorcitizen/${userId}`, updatedData); // Adjust endpoint as necessary
      fetchUsers(); // Refresh user list after editing
      handleCloseModal();
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axios.delete(`https://form.cpccbe.org/api/seniorcitizen/${userId}`); // Adjust endpoint as necessary
        fetchUsers(); // Refresh user list after deletion
      } catch (error) {
        console.error("Error deleting user:", error);
      }
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setOpenViewModal(true);
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setSelectedUser(null);
  };

  const handleExpandRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };
  
  const filteredUsers = applySortFilter(users, getComparator(order, orderBy), filterName, categoryFilter);
  
  const isUserNotFound = filteredUsers.length === 0;

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return '-';
    }
  };

  // Function to render collection summary
  const renderCollectionSummary = (collection) => {
    if (!collection || !Array.isArray(collection) || collection.length === 0) {
      return 'None';
    }
    return `${collection.length} item(s)`;
  };
  
  return (
    <Page title="Senior Citizen User">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Senior Citizen Users
          </Typography>
        </Stack>
  
        <Card>
          <SeniorCitizenListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            categoryFilter={categoryFilter}
            onCategoryFilterChange={(e) => setCategoryFilter(e.target.value)}
          />
  
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={users.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const {
                      _id,
                      name,
                      fatherOrHusbandName,
                      email,
                      phoneNumber,
                      address,
                      pinCode,
                      organization,
                      preferredMailingAddress,
                      category,
                      gender,
                      dateOfBirth,
                      age,
                      qualification,
                      currentDesignation,
                      experienceYears,
                      pastExperience,
                      papersPublished,
                      memberships,
                      additionalInfo,
                      introducedBy,
                      paymentdetails,
                      MSMECertificate,
                      panNumber,
                      companyName,
                      natureOfIndustry,
                      numberOfEmployees,
                      howDidYouKnowAboutUs,
                      membershipID,
                      membershipCategory,
                      tenure,
                    } = row;
  
                    const isItemSelected = selected.indexOf(email) !== -1;
                    const isRowExpanded = expandedRow === _id;
  
                    return (
                      <>
                        <TableRow
                          hover
                          key={_id}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                        >
                          <TableCell align="left">
                            <IconButton onClick={() => handleViewUser(row)} aria-label="view user">
                              <Iconify icon="eva:eye-fill" />
                            </IconButton>
                          </TableCell>
                          <TableCell align="left">{name || '-'}</TableCell>
                          <TableCell align="left">{fatherOrHusbandName || '-'}</TableCell>
                          <TableCell align="left">{email || '-'}</TableCell>
                          <TableCell align="left">{phoneNumber || '-'}</TableCell>
                          <TableCell align="left">{address || '-'}</TableCell>
                          <TableCell align="left">{pinCode || '-'}</TableCell>
                          <TableCell align="left">{category || '-'}</TableCell>
                          <TableCell align="left">{gender || '-'}</TableCell>
                          <TableCell align="left">{formatDate(dateOfBirth)}</TableCell>
                          <TableCell align="left">{age || '-'}</TableCell>
                          <TableCell align="left">{qualification || '-'}</TableCell>
                          <TableCell align="left">{currentDesignation || '-'}</TableCell>
                          <TableCell align="left">{experienceYears || '-'}</TableCell>
                          <TableCell align="left">
                            <Tooltip title="Click to view details">
                              <Button 
                                size="small" 
                                variant="outlined" 
                                onClick={() => handleExpandRow(_id)}
                                color={pastExperience?.length > 0 ? "primary" : "inherit"}
                              >
                                {renderCollectionSummary(pastExperience)}
                              </Button>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="left">
                            <Tooltip title="Click to view details">
                              <Button 
                                size="small" 
                                variant="outlined" 
                                onClick={() => handleExpandRow(_id)}
                                color={papersPublished?.length > 0 ? "primary" : "inherit"}
                              >
                                {renderCollectionSummary(papersPublished)}
                              </Button>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="left">
                            <Tooltip title="Click to view details">
                              <Button 
                                size="small" 
                                variant="outlined" 
                                onClick={() => handleExpandRow(_id)}
                                color={memberships?.length > 0 ? "primary" : "inherit"}
                              >
                                {renderCollectionSummary(memberships)}
                              </Button>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="left">{organization?.name || '-'}</TableCell>
                          <TableCell align="left">{organization?.address || '-'}</TableCell>
                          <TableCell align="left">{organization?.phone || '-'}</TableCell>
                          <TableCell align="left">{organization?.mobile || '-'}</TableCell>
                          <TableCell align="left">{organization?.email || '-'}</TableCell>
                          <TableCell align="left">{preferredMailingAddress || '-'}</TableCell>
                          <TableCell align="left">{additionalInfo || '-'}</TableCell>
                          <TableCell align="left">
                            {introducedBy ? 
                              `${introducedBy.name || '-'} (${introducedBy.membershipCertificateNumber || '-'})` : 
                              '-'}
                          </TableCell>
                          <TableCell align="left">{paymentdetails || '-'}</TableCell>
                          <TableCell align="left">{MSMECertificate || '-'}</TableCell>
                          <TableCell align="left">{panNumber || '-'}</TableCell>
                          <TableCell align="left">{companyName || '-'}</TableCell>
                          <TableCell align="left">{natureOfIndustry || '-'}</TableCell>
                          <TableCell align="left">{numberOfEmployees || '-'}</TableCell>
                          <TableCell align="left">{howDidYouKnowAboutUs || '-'}</TableCell>
                          <TableCell align="left">{membershipID || '-'}</TableCell>
                          <TableCell align="left">{membershipCategory || '-'}</TableCell>
                          <TableCell align="left">{tenure || '-'}</TableCell>
                          <TableCell align="left">
                            <UserMoreMenu 
                              onEdit={() => handleOpenModal(row._id)} 
                              onDelete={() => handleDeleteUser(row._id)} 
                            />
                          </TableCell>
                        </TableRow>

                        {/* Expanded Row Details */}
                        {isRowExpanded && (
                          <TableRow>
                            <TableCell colSpan={TABLE_HEAD.length} sx={{ py: 3 }}>
                              <Collapse in={isRowExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 2 }}>
                                  {/* Past Experience Details */}
                                  {pastExperience && pastExperience.length > 0 && (
                                    <>
                                      <Typography variant="h6" gutterBottom component="div">
                                        Past Experience
                                      </Typography>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>From</TableCell>
                                            <TableCell>To</TableCell>
                                            <TableCell>Designation</TableCell>
                                            <TableCell>Details</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {pastExperience.map((exp, index) => (
                                            <TableRow key={index}>
                                              <TableCell>{formatDate(exp.from)}</TableCell>
                                              <TableCell>{formatDate(exp.to)}</TableCell>
                                              <TableCell>{exp.designation || '-'}</TableCell>
                                              <TableCell>{exp.details || '-'}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                      <Box sx={{ mb: 3 }} />
                                    </>
                                  )}

                                  {/* Papers Published Details */}
                                  {papersPublished && papersPublished.length > 0 && (
                                    <>
                                      <Typography variant="h6" gutterBottom component="div">
                                        Papers Published
                                      </Typography>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Title</TableCell>
                                            <TableCell>Occasion</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Remarks</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {papersPublished.map((paper, index) => (
                                            <TableRow key={index}>
                                              <TableCell>{paper.title || '-'}</TableCell>
                                              <TableCell>{paper.occasion || '-'}</TableCell>
                                              <TableCell>{formatDate(paper.date)}</TableCell>
                                              <TableCell>{paper.remarks || '-'}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                      <Box sx={{ mb: 3 }} />
                                    </>
                                  )}

                                  {/* Memberships Details */}
                                  {memberships && memberships.length > 0 && (
                                    <>
                                      <Typography variant="h6" gutterBottom component="div">
                                        Memberships
                                      </Typography>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Institution</TableCell>
                                            <TableCell>Category</TableCell>
                                            <TableCell>Professional Remarks</TableCell>
                                            <TableCell>Social Service Category</TableCell>
                                            <TableCell>Remarks</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {memberships.map((membership, index) => (
                                            <TableRow key={index}>
                                              <TableCell>{membership.institution || '-'}</TableCell>
                                              <TableCell>{membership.category || '-'}</TableCell>
                                              <TableCell>{membership.professionalRemarks || '-'}</TableCell>
                                              <TableCell>{membership.socialServiceCategory || '-'}</TableCell>
                                              <TableCell>{membership.remarks || '-'}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                  {isUserNotFound && (
                    <TableRow>
                      <TableCell align="center" colSpan={TABLE_HEAD.length + 1} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
  
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={users.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
        <SeniorCitizenUserDialog 
          open={openModal} 
          onClose={handleCloseModal} 
          userId={editingUserId} 
          fetchUsers={fetchUsers} 
        />
        <SeniorCitizenUserViewDialog
          open={openViewModal}
          user={selectedUser}
          onClose={handleCloseViewModal}
        />
      </Container>
    </Page>
  );
}